import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { isAfter, isBefore, startOfDay, endOfDay } from 'date-fns';
import debounce from 'lodash/debounce';
import { DataEntry, ReviewTransactionFilters } from '../types';

interface UsePersonalBusinessFiltersProps {
  startDate: Date;
  endDate: Date;
}

interface UsePersonalBusinessFiltersReturn {
  filterState: ReviewTransactionFilters;
  handleFilterChange: (
    newFilterState:
      | ReviewTransactionFilters
      | ((prev: ReviewTransactionFilters) => ReviewTransactionFilters),
  ) => void;
  handleResetFilters: () => void;
  isIncludedWithFilters: (data: DataEntry) => boolean;
  searchInputRef: React.RefObject<HTMLInputElement>;
}

const doesDataEntryContainSearchString = (entry: DataEntry, search: string): boolean => {
  const searchLower = search.toLowerCase();
  return (
    entry.journalEntry.vendorName.toLowerCase().indexOf(searchLower) !== -1 ||
    entry.ledgerAccount.name.toLowerCase().indexOf(searchLower) !== -1 ||
    String(entry.amount).indexOf(searchLower) !== -1
  );
};

export const defaultFilterState: ReviewTransactionFilters = {
  search: null,
  accounts: null,
  startDate: startOfDay(new Date(new Date().getFullYear(), 0, 1)),
  endDate: endOfDay(new Date()),
  classification: null,
};

export const usePersonalBusinessFilters = ({
  startDate,
  endDate,
}: UsePersonalBusinessFiltersProps): UsePersonalBusinessFiltersReturn => {
  const [filterState, setFilterState] = useState<ReviewTransactionFilters>({
    ...defaultFilterState,
    startDate: startOfDay(startDate),
    endDate: endOfDay(endDate),
  });

  const searchInputRef = useRef<HTMLInputElement>(null);

  const debouncedSearchChange = useMemo(
    () =>
      debounce((searchValue: string | null) => {
        setFilterState(prev => ({ ...prev, search: searchValue }));
      }, 300),
    [],
  );

  // Immediate filter change handler for non-search filters
  const handleFilterChange = useCallback(
    (
      updater:
        | ReviewTransactionFilters
        | ((prev: ReviewTransactionFilters) => ReviewTransactionFilters),
    ) => {
      if (typeof updater === 'function') {
        setFilterState(prevState => {
          const newState = updater(prevState);
          if (newState.search !== prevState.search) {
            debouncedSearchChange(newState.search);
            return { ...newState, search: prevState.search };
          }
          return newState;
        });
      } else {
        const newFilterState = updater;
        setFilterState(prevState => {
          if (newFilterState.search !== prevState.search) {
            debouncedSearchChange(newFilterState.search);
            return { ...newFilterState, search: prevState.search };
          }
          return newFilterState;
        });
      }
    },
    [debouncedSearchChange],
  );

  const isIncludedWithFilters = useCallback(
    (data: DataEntry) => {
      if (filterState.search && !doesDataEntryContainSearchString(data, filterState.search))
        return false;
      if (
        filterState.accounts &&
        filterState.accounts.length > 0 &&
        !filterState.accounts.some(acc => acc.id === data.ledgerAccount.id)
      )
        return false;
      if (
        filterState.classification &&
        data.journalEntry.classification.value !== filterState.classification
      )
        return false;
      if (
        filterState.startDate &&
        isBefore(new Date(data.journalEntry.dateOfTransaction), filterState.startDate)
      )
        return false;
      if (
        filterState.endDate &&
        isAfter(new Date(data.journalEntry.dateOfTransaction), filterState.endDate)
      )
        return false;
      return true;
    },
    [filterState],
  );

  const handleResetFilters = useCallback(() => {
    const resetState = {
      ...defaultFilterState,
      startDate: startOfDay(startDate),
      endDate: endOfDay(endDate),
      classification: null,
    };
    setFilterState(resetState);
    debouncedSearchChange.cancel?.();
    if (searchInputRef.current) {
      searchInputRef.current.value = '';
    }
  }, [debouncedSearchChange, startDate, endDate]);

  useEffect(() => {
    return () => {
      debouncedSearchChange.cancel?.();
    };
  }, [debouncedSearchChange]);

  return {
    filterState,
    handleFilterChange,
    handleResetFilters,
    isIncludedWithFilters,
    searchInputRef,
  };
};
